import { ElectionStatusEnum } from '../enums/ElectionStatusEnum';
import { IHAChange } from '../interfaces/IHAChange';
import { IHAStatic } from '../interfaces/IHAStatic';
import { DistrictCount } from './DistrictCount';
import { PartyCount } from './PartyCount';

export class ElectionCount {
  private readonly _haStatic: IHAStatic;
  private readonly _haChange: IHAChange;

  private totalPollingBoothsValue = 0;
  private pollingBoothsWithCountsValue = 0;
  private totalEarlyVotingCentresValue = 0;
  private earlyVotingCentresWithCountsValue = 0;
  private totalDeclarationTypesValue = 0;
  private declarationTypesWithCountsValue = 0;

  private totalEnrolledValue = 0;
  private totalVotesCastValue = 0;
  private totalFormalVotesValue = 0;
  private totalInformalVotesValue = 0;

  public parties: PartyCount[] = [];

  public districts: DistrictCount[] = [];

  constructor(haStatic: IHAStatic, haChange: IHAChange) {
    this._haStatic = haStatic;
    this._haChange = haChange;
    const tmpDistricts = this._haStatic.districts.map(
      (item) => item.districtName
    );
    tmpDistricts.forEach((district) => {
      const haStaticDistrict = this._haStatic.districts.find(
        (x) => x.districtName === district
      );
      const haChangeDistrict = this._haChange.districts.find(
        (x) => x.districtId === district
      );
      if (haStaticDistrict && haChangeDistrict) {
        this.districts.push(
          new DistrictCount(
            this._haStatic.electionDate,
            haStaticDistrict,
            haChangeDistrict
          )
        );
      }
    });
    this.afterInit();
  }

  public afterInit(): void {
    this.districts.forEach((district) => district.afterInit());

    // calc totals here
    this.totalEnrolledValue = this.districts.reduce(
      (sum, current) => sum + current.districtEnrolled,
      0
    );
    this.totalFormalVotesValue = this.districts.reduce(
      (sum, current) => sum + current.districtCandidatesVotes,
      0
    );
    this.totalInformalVotesValue = this.districts.reduce(
      (sum, current) => sum + current.districtInformalVotes,
      0
    );
    this.totalVotesCastValue =
      this.totalFormalVotesValue + this.totalInformalVotesValue;

    this.totalPollingBoothsValue = this.districts.reduce(
      (sum, current) => sum + current.districtPollingBooths,
      0
    );
    this.pollingBoothsWithCountsValue = this.districts.reduce(
      (sum, current) => sum + current.districtPollingBoothsWithCounts,
      0
    );

    this.totalEarlyVotingCentresValue = this.districts.reduce(
      (sum, current) => sum + current.districtEarlyVotingCentres,
      0
    );
    this.earlyVotingCentresWithCountsValue = this.districts.reduce(
      (sum, current) => sum + current.districtEarlyVotingCentresWithCounts,
      0
    );

    this.totalDeclarationTypesValue = this.districts.reduce(
      (sum, current) => sum + current.districtDeclarationTypes,
      0
    );
    this.declarationTypesWithCountsValue = this.districts.reduce(
      (sum, current) => sum + current.districtDeclarationTypesWithCounts,
      0
    );

    const partyNames: string[] = [];
    this.districts.forEach((district) => {
      district.candidates.forEach((candidate) => {
        partyNames.push(candidate.partyName);
      });
    });
    const uniquePartyNames = partyNames.filter(
      (item, i, ar) => ar.indexOf(item) === i
    );
    uniquePartyNames.forEach((uniquePartyName) => {
      const tmpPartyCount = new PartyCount();
      tmpPartyCount.firstPrefVotes = 0;
      tmpPartyCount.leadingDistrictSeatCount = this.districts.filter(
        (x) => x.leadingCandidate?.partyName === uniquePartyName
      ).length;
      tmpPartyCount.winningPreviousSeatCount = this.districts.filter(
        (x) => x.previousPartyName === uniquePartyName
      ).length;

      tmpPartyCount.leadingDistricts = this.districts
        .filter((x) => x.leadingCandidate?.partyName === uniquePartyName)
        .map((x) => x.districtName)
        .join(', ');
      tmpPartyCount.totalSeats = this.districts.length;
      let foundParty = false;
      this.districts.forEach((district) => {
        const tmpCandidate2 = district.candidates.find(
          (x) => x.partyName === uniquePartyName
        );
        if (tmpCandidate2) {
          if (foundParty === false) {
            foundParty = true;
            tmpPartyCount.partyId = tmpCandidate2.partyId;
            (tmpPartyCount.partyName = uniquePartyName),
              (tmpPartyCount.colourCode = tmpCandidate2.colourCode);
            tmpPartyCount.firstPrefShare = 0;
            tmpPartyCount.totalPeviousCount = 0;
          }
          tmpPartyCount.firstPrefVotes +=
            tmpCandidate2.ordinaryVoteCount +
            tmpCandidate2.declarationVoteCount;
          tmpPartyCount.firstPrefPreviousCount +=
            tmpCandidate2.previousFirstPrefVotes === null
              ? 0
              : tmpCandidate2.previousFirstPrefVotes;
          tmpPartyCount.totalPeviousCount +=
            district.previousTotalFirstPrefVotes;
        }
        tmpPartyCount.firstPrefShare =
          district.districtTotalFormalVotes > 0
            ? tmpPartyCount.firstPrefVotes / district.districtTotalFormalVotes
            : 0;
      });
      if (foundParty) {
        this.parties.push(tmpPartyCount);
      }
    });
    if (this.totalFormalVotesValue > 1) {
      const tmpLeadingTotalSeatCount = this.parties.reduce(
        (sum, current) => sum + current.leadingDistrictSeatCount,
        0
      );
      this.parties.forEach((party) => {
        party.firstPrefShare =
          (party.firstPrefVotes / this.totalFormalVotesValue) * 100;
        party.totalLeadingSeatCount = tmpLeadingTotalSeatCount;
      });
    }

    // this.parties.sort((a, b) => b.totalSeats - a.totalSeats);
  }

  public get baseHaStatic(): IHAStatic {
    return this._haStatic;
  }

  public get baseHaChange(): IHAChange {
    return this._haChange;
  }

  public get electionType(): string {
    return this._haChange.electionType;
  }

  public get electionDate(): Date {
    return this._haChange.electionDate;
  }

  public get electionStatus(): ElectionStatusEnum {
    return this._haChange.electionStatus;
  }

  public get dataVersion(): number {
    return this._haChange.dataVersion;
  }

  public get totalPollingBooths(): number {
    return this.totalPollingBoothsValue;
  }

  public get pollingBoothsWithCounts(): number {
    return this.pollingBoothsWithCountsValue;
  }

  public get pollingBoothCountPercent(): number {
    return this.totalPollingBoothsValue > 0
      ? (this.pollingBoothsWithCountsValue / this.totalPollingBoothsValue) * 100
      : 0;
  }

  public get totalEarlyVotingCentres(): number {
    return this.totalEarlyVotingCentresValue;
  }

  public get earlyVotingCentresWithCounts(): number {
    return this.earlyVotingCentresWithCountsValue;
  }

  public get earlyVotingCentreCountPercent(): number {
    return this.totalEarlyVotingCentresValue > 0
      ? (this.earlyVotingCentresWithCountsValue /
          this.totalEarlyVotingCentresValue) *
          100
      : 0;
  }

  public get totalDeclarationTypes(): number {
    return this.totalDeclarationTypesValue;
  }

  public get declarationTypesWithCounts(): number {
    return this.declarationTypesWithCountsValue;
  }

  public get declarationTypeCountPercent(): number {
    return this.totalDeclarationTypesValue > 0
      ? (this.declarationTypesWithCountsValue /
          this.totalDeclarationTypesValue) *
          100
      : 0;
  }

  public get totalEnrolled(): number {
    return this.totalEnrolledValue;
  }

  public get totalVostesCast(): number {
    return this.totalVotesCastValue;
  }

  public get totalFormalVotes(): number {
    return this.totalFormalVotesValue;
  }

  public get totalInformalVotes(): number {
    return this.totalInformalVotesValue;
  }

  public get formalVotesPercent(): number {
    return this.totalFormalVotes + this.totalInformalVotesValue > 0
      ? (this.totalFormalVotes /
          (this.totalFormalVotes + this.totalInformalVotesValue)) *
          100
      : 0;
  }

  public get informalVotesPercent(): number {
    return this.totalFormalVotes + this.totalInformalVotesValue > 0
      ? (this.totalInformalVotesValue /
          (this.totalFormalVotes + this.totalInformalVotesValue)) *
          100
      : 0;
  }

  public get turnoutPercent(): number {
    return this.totalEnrolledValue > 0 && this.totalVotesCastValue > 0
      ? (this.totalVotesCastValue / this.totalEnrolledValue) * 100
      : 0;
  }

  public getDistrict(districtName: string | null): DistrictCount | null {
    if (districtName === null) {
      return this.districts.length > 0 ? this.districts[0] : null;
    }
    return this.districts.find((x) => x.districtName === districtName) ?? null;
  }

  public get districtList(): string[] {
    return this.districts.map((x) => x.districtName);
  }
}
